import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { api } from '../../api';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import useChatEvents from '../../hooks/useChatEvents';
import ChatMessage from '../../components/ChatMessage';
import ChatScopeBanner from '../../components/ChatScopeBanner';
import TierScopePrompt from '../../components/TierScopePrompt';
import BackgroundJobsBanner from '../../components/BackgroundJobsBanner';
import BudgetExceededBanner from '../../components/BudgetExceededBanner';
import SpendCapBanner from '../../components/SpendCapBanner';
import ScopeConfirmationPrompt from '../../components/ScopeConfirmationPrompt';
import { AGENT_NAME, AGENT_NAME_SHORT } from '../../brand';
import ConversationSelector from '../../components/ConversationSelector';
import FolderPlanCard from '../../components/FolderPlanCard';
import SavedWorkModal from '../../components/SavedWork';
import ArcOrb from '../../components/ArcOrb';
import useHelpContextSignal from '../../hooks/useHelpContextSignal';

/* ── Human-friendly tool name labels ── */
const TOOL_LABELS = {
  search_documents: 'Searching documents',
  semantic_search: 'Semantic search',
  get_case_overview: 'Reviewing case overview',
  get_coverage_status: 'Checking coverage status',
  read_document: 'Reading document',
  list_documents: 'Listing documents',
  index_all_documents: 'Indexing all documents for search',
  complete_extraction: 'Upgrading to deep search',
  get_document_summary: 'Reading document summary',
  organize_documents: 'Organizing documents',
  categorize_documents: 'Categorizing documents',
  apply_folder_plan: 'Applying folder structure',
  save_artifact: 'Saving to Saved work',
  update_artifact: 'Updating saved work',
  get_artifacts: 'Checking saved work',
  delete_documents: 'Deleting documents',
  reprocess_documents: 'Reprocessing documents',
};
function toolLabel(name) { return TOOL_LABELS[name] || name?.replace(/_/g, ' ') || 'working'; }

function formatCost(usd, marginMultiplier = 1) {
  if (usd == null) return '—';
  const val = usd * marginMultiplier;
  if (val < 0.01) return `$${val.toFixed(4)}`;
  return `$${val.toFixed(2)}`;
}

/* ── Event processor (mirrors _process_events_into_state from Streamlit) ── */

function processEvents(events) {
  let text = '';
  let plan = '';
  const toolCalls = [];
  const toolResults = [];
  const progress = [];
  let done = null;
  let error = null;
  let costEstimate = null;
  let citations = [];
  let suggestions = [];
  let clarification = null;
  let folderDirty = false;
  let checklistDirty = false;
  // Latest propose_query_scope result, if any. Used to surface
  // pickable scope cards alongside follow-up suggestions after the
  // run finalises. We keep the latest so multi-tool runs (e.g.
  // timeline + entities) collapse to the most recent.
  let proposedScope = null;

  for (const evt of events) {
    const t = evt.event_type || evt.type;
    const d = evt.data ?? evt;

    switch (t) {
      case 'streaming_text':
        text += d.text ?? d.chunk ?? '';
        break;
      case 'token':
        // react_loop emits answer text as it arrives — including text the
        // model writes alongside tool calls (transcript-first contract).
        // Payload is the raw chunk string.
        text += typeof d === 'string' ? d : (d.text ?? d.chunk ?? '');
        break;
      case 'plan':
        plan = d.plan ?? d.text ?? '';
        break;
      case 'tool_call':
        toolCalls.push({
          tool_name: d.tool_name ?? d.name ?? d.tool,
          args: d.args ?? d.arguments ?? {},
          sequence: evt.sequence,
        });
        progress.push({ type: 'step_started', text: `${toolLabel(d.tool_name ?? d.name ?? d.tool)}…` });
        break;
      case 'tool_result': {
        const trName = d.tool_name ?? d.name ?? d.tool;
        const trResult = d.result ?? d.output;
        toolResults.push({
          tool_name: trName,
          result: trResult,
          duration_ms: d.duration_ms,
          sequence: evt.sequence,
        });
        // Capture the scope-companion output so the UI can render
        // pickable scope cards after the run finalises.
        if (trName === 'propose_query_scope' && trResult && trResult.suggested_scopes) {
          proposedScope = trResult;
        }
        const name = toolLabel(trName);
        const ms = d.duration_ms;
        progress.push({ type: 'step_complete', text: ms ? `${name} (${ms}ms)` : name });
        // Flag that folder-mutating tools completed so we can refresh the tree
        if (['apply_folder_plan', 'organize_documents', 'create_folder',
             'rename_folder', 'delete_folder'].includes(trName)) {
          folderDirty = true;
        }
        if (['update_checklist_items', 'accept_match_candidates',
             'run_verification_pipeline', 'match_checklist',
             'match_documents_to_checklist', 'llm_match_review',
             'deduplicate_documents', 'classify_checklist_items',
             'normalize_for_matching', 'create_checklist_from_text',
             'parse_as_checklist', 'import_checklist_columns'].includes(trName)) {
          checklistDirty = true;
        }
        break;
      }
      case 'progress': {
        const step = d.step ?? d.type;
        if (step === 'finding') {
          progress.push({ type: 'finding', text: d.finding ?? d.text });
        } else if (step === 'step_started') {
          progress.push({ type: 'step_started', text: d.message ?? d.step_name });
        } else if (step === 'step_complete') {
          progress.push({ type: 'step_complete', text: d.message ?? d.step_name });
        } else if (step === 'status') {
          // Streaming heartbeats from grounded synthesis carry a
          // parenthesized count suffix that ticks every ~2s (e.g.
          // "Drafting grounded answer… (1.2k tokens, 18s)"). Collapse
          // consecutive heartbeats with the same prefix so the user
          // sees one stable line whose count updates in place, not a
          // wall of near-identical entries.
          // Drop the not-started-yet zero suffix ("(0 tokens, 0s)") — it
          // reads as a stuck counter; live counts appear once streaming does.
          const text = (d.message ?? d.text ?? '').replace(/\s*\(0 tokens, 0s\)\s*$/, '');
          const last = progress[progress.length - 1];
          const newPrefix = text.split('(')[0];
          if (last && last.type === 'step_started' && last.text.split('(')[0] === newPrefix) {
            progress[progress.length - 1] = { type: 'step_started', text };
          } else {
            progress.push({ type: 'step_started', text });
          }
        }
        // heartbeat events are silently ignored
        break;
      }
      case 'citations':
        citations = d.citations ?? [];
        break;
      case 'done':
        // Backend payload shape on done:
        //   {status, duration_ms, operation_id, answer, citations,
        //    tool_trace, suggestions, cost_usd, ...}
        // budget_exceeded / cancelled variants also carry
        //   {limit, observed, cap} for the budget banner.
        done = d;
        break;
      case 'cost_estimate':
        costEstimate = d;
        break;
      case 'suggestions':
        suggestions = d.suggestions ?? [];
        break;
      case 'clarification':
        clarification = { question: d.question, options: d.options ?? [] };
        break;
      case 'error':
        error = d.error ?? d.message ?? 'Unknown error';
        break;
      case 'plan_error':
        // The graph runtime's fatal path. It is followed by a friendlier
        // `error` event (agent_errors.user_facing_error) which overwrites
        // this; keeping the raw message here means no failure is ever silent.
        error = d.message ?? d.error ?? 'Arc stopped unexpectedly.';
        break;
      default:
        break;
    }
  }

  // Merge tool calls + results into trace
  const trace = toolCalls.map(tc => {
    const tr = toolResults.find(r => r.tool_name === tc.tool_name && r.sequence > tc.sequence);
    return { ...tc, result: tr?.result, duration_ms: tr?.duration_ms };
  });

  // Merge citations into done so the finalization callback always has them
  if (done && !done.citations?.length && citations.length) {
    done.citations = citations;
  }

  return { text, plan, trace, progress, done, error, costEstimate, citations, suggestions, clarification, folderDirty, checklistDirty, proposedScope };
}

/* ── Build filename → doc lookup ── */
function buildFilenameMap(documents) {
  const map = {};
  if (!documents) return map;
  for (const doc of documents) {
    if (doc.filename) {
      map[doc.filename] = doc;
      const stem = doc.filename.replace(/\.[^.]+$/, '');
      if (!map[stem]) map[stem] = doc;
    }
  }
  return map;
}

/* ── Main Component ── */

export default function ExplorationTab({ caseId }) {
  const {
    documents, pricing, chatScope, pendingRefine,
    chatConversationId, chatHistory, chatLastEventSeq, chatCostUsd,
    dispatch,
  } = useApp();
  const { token } = useAuth();
  // Set for an organization whose answers come from a model outside the
  // CJIS environment (its ops-set choice); shown under the input.
  const { profile } = useUserProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [streamProgress, setStreamProgress] = useState([]);
  const [streamTrace, setStreamTrace] = useState([]);
  const [streamError, setStreamError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [savedWorkOpen, setSavedWorkOpen] = useState(false);
  const [costGate, setCostGate] = useState(null);   // {message, tools, estimate}
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]);  // ["question1", ...]
  const [clarificationGate, setClarificationGate] = useState(null);   // {question, options}
  const [folderPlanGate, setFolderPlanGate] = useState(null);   // pending_action from done event
  const [folderPlanApplying, setFolderPlanApplying] = useState(false);
  const [staleWarning, setStaleWarning] = useState(false);
  // Phase 2.1 — per-turn budget cap (MAX_TURN_LLM_CALLS / wall / tokens).
  // When the agent finalises with status=budget_exceeded the backend
  // attaches {limit, observed, cap}; we render a distinct banner so
  // users see "narrow this and retry" rather than a generic error.
  const [budgetGate, setBudgetGate] = useState(null);   // {limit, observed, cap}
  // The org is at its monthly spending limit (HTTP 402): the question was
  // not started. {detail: the 402 body, text: the blocked question}.
  const [spendGate, setSpendGate] = useState(null);
  // Scope-companion output (from propose_query_scope). When non-null,
  // we render a pickable scope card next to follow-up suggestions so
  // the user can redirect the next turn without retyping.
  const [proposedScope, setProposedScope] = useState(null);

  // Help-context signals: how the user's Arc state surfaces to the help agent.
  // Counts/booleans only — never document IDs or scope contents.
  const arcScopeSize = chatScope?.document_ids?.length;
  useHelpContextSignal('arc_scope_size', typeof arcScopeSize === 'number' ? arcScopeSize : null);
  useHelpContextSignal('folder_plan_card_visible', !!folderPlanGate);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const allEventsRef = useRef([]);
  const streamingRef = useRef(false);
  const chatEventsRef = useRef(null);
  const lastEventTimeRef = useRef(Date.now());
  const staleTimerRef = useRef(null);
  const progressEndRef = useRef(null);
  // operation_id for the in-flight run. Captured from chat/start
  // response and (defensively) from agent_done. Drives the
  // operation-based cancel endpoint which has DB-backed,
  // cross-instance kill (memory: agentic_safety_net_patterns.md).
  const operationIdRef = useRef(null);

  const filenameMap = useMemo(() => buildFilenameMap(documents), [documents]);
  const marginMultiplier = pricing?.margin_multiplier || 1;

  // Keep ref in sync so polling callbacks always see the latest value
  useEffect(() => { streamingRef.current = streaming; }, [streaming]);

  // Stale-stream detection: if no events for 120s while streaming,
  // warn user. Raised from 60s because grounded_synthesis (p50 ~87s
  // on chunk-bearing turns, max ~130s in 24h data) is silent end-to-
  // end, triggering false positives on ~32% of turns. Paired with a
  // backend bookend `progress` event before grounded_synthesis runs,
  // which resets the timer at the start of the silent window.
  useEffect(() => {
    if (streaming) {
      lastEventTimeRef.current = Date.now();
      staleTimerRef.current = setInterval(() => {
        if (Date.now() - lastEventTimeRef.current > 120_000) {
          setStaleWarning(true);
        }
      }, 5_000);
    } else {
      setStaleWarning(false);
      if (staleTimerRef.current) clearInterval(staleTimerRef.current);
    }
    return () => { if (staleTimerRef.current) clearInterval(staleTimerRef.current); };
  }, [streaming]);

  // Chat events hook
  const onEvents = useCallback((events) => {
    lastEventTimeRef.current = Date.now();
    setStaleWarning(false);
    allEventsRef.current = [...allEventsRef.current, ...events];
    const state = processEvents(allEventsRef.current);

    setStreamText(state.text);
    setStreamProgress(state.progress);
    setStreamTrace(state.trace);

    if (state.error) {
      setStreamError(state.error);
      setStreaming(false);
    }

    // Clarification gate: agent needs user input to proceed
    if (state.clarification) {
      setClarificationGate(state.clarification);
      dispatch({ type: 'UPDATE_CHAT_SEQ', seq: chatEventsRef.current?.getSeq() ?? 0 });
      setStreaming(false);
      setStreamText('');
      setStreamProgress([]);
      setStreamTrace([]);
      allEventsRef.current = [];
      return;
    }

    // Cost gate: agent wants approval before proceeding
    if (state.done?.cost_gate) {
      setCostGate({
        message: state.done.cost_gate_message || 'This operation requires approval.',
        tools: state.done.tool_trace?.map(t => t.tool).filter(Boolean) || [],
        estimate: state.costEstimate,
      });
      dispatch({ type: 'UPDATE_CHAT_SEQ', seq: chatEventsRef.current?.getSeq() ?? 0 });
      setStreaming(false);
      setStreamText('');
      setStreamProgress([]);
      setStreamTrace([]);
      allEventsRef.current = [];
      return;
    }

    // Folder plan gate: agent stashed a plan and wants the user to apply it
    if (state.done?.pending_action?.type === 'apply_folder_plan') {
      setFolderPlanGate(state.done.pending_action);
      dispatch({ type: 'UPDATE_CHAT_SEQ', seq: chatEventsRef.current?.getSeq() ?? 0 });
      setStreaming(false);
      setStreamText('');
      setStreamProgress([]);
      setStreamTrace([]);
      allEventsRef.current = [];
      return;
    }

    // Defensive: capture operation_id from done events (covers
    // resumed conversations where the chat/start response is gone).
    if (state.done?.operation_id) {
      operationIdRef.current = state.done.operation_id;
    }

    // Budget-exceeded gate: a per-turn cap fired (MAX_TURN_LLM_CALLS /
    // wall_seconds / token caps). This is a "clean exit" outcome from
    // the backend — not an error. Show a distinct banner and preserve
    // whatever partial answer streamed before the cap hit.
    if (state.done?.status === 'budget_exceeded') {
      streamingRef.current = false;
      setBudgetGate({
        limit: state.done.limit || null,
        observed: state.done.observed ?? null,
        cap: state.done.cap ?? null,
      });
      if (state.text) {
        dispatch({
          type: 'APPEND_CHAT',
          msg: {
            role: 'assistant',
            content: state.text,
            operation_id: state.done.operation_id ?? operationIdRef.current,
            tool_trace: state.trace,
            partial: true,
            stop_reason: 'budget_exceeded',
          },
        });
      }
      dispatch({ type: 'UPDATE_CHAT_SEQ', seq: chatEventsRef.current?.getSeq() ?? 0 });
      setStreaming(false);
      setStreamText('');
      setStreamProgress([]);
      setStreamTrace([]);
      allEventsRef.current = [];
      return;
    }

    // Cancelled exit: user clicked Stop. Preserve partial text but
    // tag it so downstream UI can distinguish from a real completion.
    if (state.done?.status === 'cancelled') {
      streamingRef.current = false;
      if (state.text) {
        dispatch({
          type: 'APPEND_CHAT',
          msg: {
            role: 'assistant',
            content: state.text,
            operation_id: state.done.operation_id ?? operationIdRef.current,
            tool_trace: state.trace,
            partial: true,
            stop_reason: 'cancelled',
          },
        });
      }
      dispatch({ type: 'UPDATE_CHAT_SEQ', seq: chatEventsRef.current?.getSeq() ?? 0 });
      setStreaming(false);
      setStreamText('');
      setStreamProgress([]);
      setStreamTrace([]);
      allEventsRef.current = [];
      return;
    }

    if (state.done) {
      const d = state.done;
      // Reset the ref synchronously — onStatusChange can run in the same
      // JS tick as this handler (before the streaming-state effect), and
      // its fallback append must see the turn as already finalized.
      streamingRef.current = false;
      dispatch({
        type: 'APPEND_CHAT',
        msg: {
          role: 'assistant',
          content: d.answer ?? state.text,
          operation_id: d.operation_id ?? operationIdRef.current,
          tool_trace: d.tool_trace ?? state.trace,
          citations: d.citations,
          confidence: d.confidence,
          tokens_in: d.tokens_in,
          tokens_out: d.tokens_out,
          cost_usd: d.cost_usd,
          duration_ms: d.duration_ms,
          partial: d.partial,
        },
      });

      if (d.cost_usd) {
        dispatch({ type: 'ADD_CHAT_COST', cost: d.cost_usd });
      }

      // Surface follow-up suggestions as clickable buttons
      const sugg = d.suggestions || state.suggestions;
      setFollowUpSuggestions(sugg && sugg.length > 0 ? sugg : []);

      // Surface the scope-companion output (if propose_query_scope
      // ran this turn) so the user can redirect with one click.
      setProposedScope(state.proposedScope || null);

      // Refresh folder tree if any folder-mutating tool ran
      if (state.folderDirty) {
        api.get(`/documents/case/${caseId}/folder-tree`)
          .then(tree => dispatch({ type: 'SET_FOLDER_TREE', tree }))
          .catch(() => {});  // best-effort
      }

      // Notify ChecklistTab to refresh when checklist-mutating tools ran
      if (state.checklistDirty) {
        window.dispatchEvent(new CustomEvent('checklist-dirty', { detail: { caseId } }));
      }

      dispatch({ type: 'UPDATE_CHAT_SEQ', seq: chatEventsRef.current?.getSeq() ?? 0 });

      setStreaming(false);
      setStreamText('');
      setStreamProgress([]);
      setStreamTrace([]);
      allEventsRef.current = [];
    }
  }, [dispatch, caseId]);

  const onStatusChange = useCallback((status) => {
    if (status === 'completed' || status === 'interrupted') {
      // Use ref to avoid stale closure — streaming state may not
      // have been applied yet when this callback was captured.
      if (streamingRef.current) {
        const state = processEvents(allEventsRef.current);
        if (state.text) {
          // Fallback finalizer for runs whose done event never arrived.
          // Carries the operation_id so a late done event for the same
          // turn upserts over this partial instead of duplicating it.
          dispatch({
            type: 'APPEND_CHAT',
            msg: {
              role: 'assistant',
              content: state.text,
              operation_id: operationIdRef.current,
              tool_trace: state.trace,
              partial: true,
            },
          });
        }
        if (state.error) {
          setStreamError(state.error);
        }
        // Persist the max event sequence for follow-ups
        dispatch({ type: 'UPDATE_CHAT_SEQ', seq: chatEventsRef.current?.getSeq() ?? 0 });

        streamingRef.current = false;
        setStreaming(false);
        setStreamText('');
        setStreamProgress([]);
        setStreamTrace([]);
        allEventsRef.current = [];
      }
    }
  }, [dispatch]);

  const chatEvents = useChatEvents(caseId, chatConversationId, {
    getToken: () => token,
    onEvents,
    onStatusChange,
    intervalMs: 500,
  });

  // Keep chatEventsRef in sync so callbacks access getSeq() without
  // needing chatEvents in their dependency arrays (avoids circular deps).
  useEffect(() => { chatEventsRef.current = chatEvents; }, [chatEvents]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, streamText]);

  // Focus input when not streaming
  useEffect(() => {
    if (!streaming) inputRef.current?.focus();
  }, [streaming]);

  // Auto-resume: detect any streaming conversation on mount
  // On mount: refresh the current conversation (restores streaming + final answers)
  // or detect an orphaned streaming conversation if none is set.
  useEffect(() => {
    let cancelled = false;
    async function resume() {
      try {
        if (chatConversationId) {
          // Re-fetch latest messages & resume polling if still streaming
          if (!cancelled) loadConversation(chatConversationId);
          return;
        }
        // No conversation in context — check for any active stream
        const data = await api.get(`/exploration/case/${caseId}/conversations`);
        if (cancelled) return;
        const active = (data.conversations || []).find(c => c.status === 'streaming');
        if (active) loadConversation(active.id);
      } catch {
        // non-fatal
      }
    }
    resume();
    return () => { cancelled = true; };
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from the Notepad "Open source" — open the exact source
  // conversation whenever a ?conversation_id= param appears. Keyed on the
  // param (not caseId) so it also fires when the tab is already mounted and
  // only the query string changes. Strip the param after consuming so a later
  // manual "New" chat isn't re-hijacked on the next render.
  useEffect(() => {
    const deepLinkConvId = searchParams.get('conversation_id');
    if (!deepLinkConvId) return;
    const deepLinkMsgId = searchParams.get('message_id');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('conversation_id');
      next.delete('message_id');
      return next;
    }, { replace: true });
    (async () => {
      await loadConversation(deepLinkConvId);
      if (deepLinkMsgId) highlightChatMessage(deepLinkMsgId);
    })();
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to and briefly flash a chat message (deep-link from a Notepad clip).
  // The conversation has just been dispatched into context, so the DOM node may
  // not exist yet — retry on a few animation frames before giving up. Missing
  // node (old clip, truncated history, id mismatch) degrades silently to the
  // plain conversation-open that already shipped.
  function highlightChatMessage(messageId, attempt = 0) {
    const el = document.querySelector(`[data-np-chat-message="${CSS.escape(String(messageId))}"]`);
    if (!el) {
      if (attempt < 20) requestAnimationFrame(() => highlightChatMessage(messageId, attempt + 1));
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('np-clip-flash');
    setTimeout(() => el.classList.remove('np-clip-flash'), 2400);
  }

  // Load existing conversation history
  async function loadConversation(convId) {
    if (!convId) {
      dispatch({ type: 'SET_CONVERSATION', id: null, history: [], seq: 0 });
      return;
    }
    try {
      const data = await api.get(`/exploration/case/${caseId}/conversations/${convId}`);
      const history = (data.messages || []).map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        tool_trace: m.tool_trace,
        citations: m.citations,
        confidence: m.confidence,
        tokens_in: m.tokens_in,
        tokens_out: m.tokens_out,
        cost_usd: m.cost_usd,
      }));

      // Use the last_event_seq returned by the backend — no separate round-trip needed.
      const seq = data.last_event_seq || 0;

      dispatch({ type: 'SET_CONVERSATION', id: convId, history, seq });

      // If still streaming, resume polling — pass convId explicitly
      // to avoid the stale-closure bug (same fix as handleSend).
      // Stale-stream guard: if the conversation is marked streaming but
      // hasn't been updated in 3+ minutes, the backend agent has crashed
      // or otherwise died. Resuming it leaves the user with a permanent
      // "Thinking..." spinner. Skip the auto-resume in that case.
      if (data.status === 'streaming') {
        const updatedAt = data.updated_at ? new Date(data.updated_at).getTime() : 0;
        const ageMs = updatedAt > 0 ? Date.now() - updatedAt : Infinity;
        const STALE_THRESHOLD_MS = 3 * 60 * 1000;  // 3 min
        if (ageMs > STALE_THRESHOLD_MS) {
          console.warn(
            `[ExplorationTab] Skipping auto-resume of stale streaming ` +
            `conversation ${convId} — last updated ${Math.round(ageMs / 1000)}s ago.`
          );
        } else {
          setStreaming(true);
          allEventsRef.current = [];
          chatEvents.start(seq, convId);
        }
      }
    } catch {
      dispatch({ type: 'SET_CONVERSATION', id: convId, history: [], seq: 0 });
    }
  }

  // Send a message
  async function handleSend(e) {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    // Add user message to history
    dispatch({ type: 'APPEND_CHAT', msg: { role: 'user', content: text } });
    setInput('');
    streamingRef.current = true;
    setStreaming(true);
    setStreamText('');
    setStreamProgress([]);
    setStreamTrace([]);
    setStreamError(null);
    setCostGate(null);
    setSpendGate(null);
    setFollowUpSuggestions([]);
    setClarificationGate(null);
    allEventsRef.current = [];

    try {
      const body = {
        message: text,
        conversation_id: chatConversationId || undefined,
      };

      // Attach scope
      if (chatScope) {
        if (chatScope.document_ids?.length) body.document_scope = chatScope.document_ids;
        if (chatScope.folder_paths?.length) body.folder_scope = chatScope.folder_paths;
        if (chatScope.refine_targets?.length) body.refine_targets = chatScope.refine_targets;
      }

      const res = await api.post(`/exploration/case/${caseId}/chat/start`, body);
      const convId = res.conversation_id;
      // Capture operation_id so the Stop button can use the
      // DB-backed cross-instance cancel endpoint.
      operationIdRef.current = res.operation_id || null;
      // Fresh run — clear any leftover budget banner / scope prompt
      // from the prior turn.
      setBudgetGate(null);
      setProposedScope(null);

      if (!chatConversationId) {
        // chatHistory is the pre-dispatch snapshot (APPEND_CHAT hasn't
        // flushed yet), so we include the user message explicitly.
        dispatch({
          type: 'SET_CONVERSATION',
          id: convId,
          history: [...chatHistory, { role: 'user', content: text }],
          seq: chatLastEventSeq,
        });
      }

      // Start polling events — pass convId explicitly so the poll URL
      // uses the real conversation ID, not the stale closure value.
      chatEvents.start(chatLastEventSeq, convId);
    } catch (err) {
      onStartFailed(err, text);
    }
  }

  // A turn failed to start. A 402 ALLOTMENT_EXCEEDED is the org's spending
  // limit, not a fault: hold the question behind the "authorize more" banner
  // instead of showing an error.
  function onStartFailed(err, text) {
    if (err.code === 'ALLOTMENT_EXCEEDED') setSpendGate({ detail: err.detail || {}, text });
    else setStreamError(err.message);
    setStreaming(false);
  }

  // More was authorized (or an admin acted elsewhere) — re-send the blocked
  // question. It is already in the transcript, so it is not appended again.
  async function resendAfterSpendGate() {
    const text = spendGate?.text;
    setSpendGate(null);
    if (!text || streaming) return;
    streamingRef.current = true;
    setStreaming(true);
    setStreamText('');
    setStreamProgress([]);
    setStreamTrace([]);
    setStreamError(null);
    allEventsRef.current = [];
    try {
      const body = { message: text, conversation_id: chatConversationId || undefined };
      if (chatScope) {
        if (chatScope.document_ids?.length) body.document_scope = chatScope.document_ids;
        if (chatScope.folder_paths?.length) body.folder_scope = chatScope.folder_paths;
        if (chatScope.refine_targets?.length) body.refine_targets = chatScope.refine_targets;
      }
      const res = await api.post(`/exploration/case/${caseId}/chat/start`, body);
      operationIdRef.current = res.operation_id || null;
      setBudgetGate(null);
      setProposedScope(null);
      if (!chatConversationId) {
        dispatch({
          type: 'SET_CONVERSATION',
          id: res.conversation_id,
          history: chatHistory,
          seq: chatLastEventSeq,
        });
      }
      chatEvents.start(chatLastEventSeq, res.conversation_id);
    } catch (err) {
      onStartFailed(err, text);
    }
  }

  // Send a message programmatically (for cost gate responses)
  function handleSendDirect(text) {
    setInput(text);
    // Use setTimeout so the state flushes before the form submit logic runs
    setTimeout(() => {
      dispatch({ type: 'APPEND_CHAT', msg: { role: 'user', content: text } });
      setInput('');
      streamingRef.current = true;
      setStreaming(true);
      setStreamText('');
      setStreamProgress([]);
      setStreamTrace([]);
      setStreamError(null);
      setSpendGate(null);
      setFollowUpSuggestions([]);
      setClarificationGate(null);
      allEventsRef.current = [];

      const body = {
        message: text,
        conversation_id: chatConversationId || undefined,
      };
      if (chatScope) {
        if (chatScope.document_ids?.length) body.document_scope = chatScope.document_ids;
        if (chatScope.folder_paths?.length) body.folder_scope = chatScope.folder_paths;
        if (chatScope.refine_targets?.length) body.refine_targets = chatScope.refine_targets;
      }
      api.post(`/exploration/case/${caseId}/chat/start`, body)
        .then(res => {
          operationIdRef.current = res.operation_id || null;
          setBudgetGate(null);
          setProposedScope(null);
          chatEvents.start(chatLastEventSeq, res.conversation_id);
        })
        .catch(err => onStartFailed(err, text));
    }, 0);
  }

  // Pick up a "Refine with agent" handoff from the Witnesses tab. The roster
  // already stashed the witness scope into chatScope and a starter message into
  // pendingRefine; auto-send it once, when we're not mid-stream. ExplorationTab
  // is always mounted, so this fires even though the click was on another tab.
  const refineFiredRef = useRef(false);
  useEffect(() => {
    if (!pendingRefine?.message) { refineFiredRef.current = false; return; }
    if (streaming || refineFiredRef.current) return;
    refineFiredRef.current = true;
    handleSendDirect(pendingRefine.message);
    dispatch({ type: 'CLEAR_PENDING_REFINE' });
  }, [pendingRefine, streaming]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel running chat.
  //
  // Prefer ``POST /agent/runs/{operation_id}/cancel`` — that path
  // writes a DB-backed cancellation flag any worker can see at its
  // next checkpoint, which means it works even when the request
  // lands on a different Cloud Run instance from the one running
  // the agent. Falls back to the legacy conversation-based cancel
  // when we don't have an operation_id yet (e.g. an old conversation
  // resumed before this build shipped the capture).
  //
  // ``conversation_id`` is passed as a query param so the cancel
  // endpoint can finalize ``conversations.status`` from its own DB
  // session — without it, a pool-starved agent task can never write
  // the terminal status and the chat sits in 'streaming' forever.
  async function handleCancel() {
    const opId = operationIdRef.current;
    try {
      if (opId) {
        const qs = chatConversationId
          ? `?conversation_id=${encodeURIComponent(chatConversationId)}`
          : '';
        await api.post(`/agent/runs/${opId}/cancel${qs}`);
      } else if (chatConversationId) {
        await api.post(`/exploration/case/${caseId}/chat/cancel/${chatConversationId}`);
      } else {
        return;
      }
    } catch {
      // ignore — the stream will either pick up the cancelled status
      // at its next checkpoint or time out; we still finalise locally.
    }
    chatEvents.stop();
    // Finalize partial text
    if (streamText) {
      dispatch({
        type: 'APPEND_CHAT',
        msg: { role: 'assistant', content: streamText, tool_trace: streamTrace, partial: true },
      });
    }
    setStreaming(false);
    setStreamText('');
    setStreamProgress([]);
    setStreamTrace([]);
    allEventsRef.current = [];
  }

  const hasMessages = chatHistory.length > 0 || streamText;
  const placeholder = chatHistory.length > 0 ? 'Ask a follow-up…' : 'Ask Arc about your case…';

  return (
    <div className="exploration-tab">
      {/* Sidebar toggle */}
      <div className="exploration-layout">
        {sidebarOpen && (
          <div className="exploration-sidebar">
            <ConversationSelector
              caseId={caseId}
              activeId={chatConversationId}
              onSelect={(id) => {
                loadConversation(id);
                setSidebarOpen(false);
              }}
            />
          </div>
        )}

        <div className="exploration-main">
          {/* Header row */}
          <div className="exploration-toolbar">
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => setSidebarOpen(o => !o)}
              title="Conversations"
            >
              {sidebarOpen ? '◁' : '☰'} History
            </button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => setSavedWorkOpen(true)}
              title={`Summaries, timelines and lists ${AGENT_NAME_SHORT} saved for this case`}
            >
              📎 Saved work
            </button>
          </div>
          {savedWorkOpen && (
            <SavedWorkModal
              caseId={caseId}
              onClose={() => setSavedWorkOpen(false)}
              onOpenConversation={(id) => {
                setSavedWorkOpen(false);
                loadConversation(id);
              }}
            />
          )}

          <ChatScopeBanner caseId={caseId} />
          <TierScopePrompt caseId={caseId} />
          <BackgroundJobsBanner caseId={caseId} />
          {spendGate && (
            <SpendCapBanner
              detail={spendGate.detail}
              onAuthorized={resendAfterSpendGate}
              onDismiss={() => setSpendGate(null)}
            />
          )}
          {budgetGate && (
            <BudgetExceededBanner
              limit={budgetGate.limit}
              observed={budgetGate.observed}
              cap={budgetGate.cap}
              onDismiss={() => setBudgetGate(null)}
            />
          )}

          {/* Messages area */}
          <div className="chat-messages">
            {!hasMessages && (
              <div className="chat-empty">
                <h3>{AGENT_NAME}</h3>
                <p>Ask questions about your case documents. {AGENT_NAME_SHORT} will search, analyze, and cite sources.</p>
              </div>
            )}

            {chatHistory.map((msg, i) => {
              // Detect if this is a follow-up question (user message, not first, preceded by assistant response)
              const isFollowUp = msg.role === 'user' && i > 0;
              
              return (
                <div key={i}>
                  {/* Separator between exchanges */}
                  {isFollowUp && (
                    <div className="chat-exchange-separator">
                      <div className="separator-line" />
                      <div className="separator-badge">Follow-up</div>
                      <div className="separator-line" />
                    </div>
                  )}
                  <ChatMessage
                    msg={msg}
                    filenameMap={filenameMap}
                    marginMultiplier={marginMultiplier}
                    token={token}
                    isFollowUp={isFollowUp}
                    messageIndex={i}
                    citations={msg.citations}
                  />
                </div>
              );
            })}

            {/* Cost gate approval dialog */}
            {costGate && (
              <div className="chat-message chat-message-assistant cost-gate-message">
                <div className="chat-message-header">
                  <span className="chat-role">{AGENT_NAME_SHORT}</span>
                  <span className="cost-gate-badge">⏸ Approval Required</span>
                </div>
                <div className="chat-message-body">
                  <ReactMarkdown
                    components={{
                      a: ({ href, children, ...props }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                      ),
                    }}
                  >{costGate.message}</ReactMarkdown>
                </div>
                <div className="cost-gate-actions">
                  <button className="btn btn-primary" onClick={() => {
                    setCostGate(null);
                    handleSendDirect('Yes, proceed with full analysis');
                  }}>Approve</button>
                  <button className="btn btn-ghost" onClick={() => {
                    setCostGate(null);
                    handleSendDirect('Reduce the scope');
                  }}>Reduce Scope</button>
                  <button className="btn btn-danger" onClick={() => {
                    setCostGate(null);
                    handleSendDirect('Cancel this operation');
                  }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Clarification gate: agent needs user input */}
            {clarificationGate && (
              <div className="chat-message chat-message-assistant clarification-gate-message">
                <div className="chat-message-header">
                  <span className="chat-role">{AGENT_NAME_SHORT}</span>
                  <span className="clarification-badge">❓ Clarification Needed</span>
                </div>
                <div className="chat-message-body">
                  <p>{clarificationGate.question}</p>
                </div>
                {clarificationGate.options.length > 0 && (
                  <div className="clarification-actions">
                    {clarificationGate.options.map((opt, i) => (
                      <button key={i} className="btn btn-ghost" onClick={() => {
                        setClarificationGate(null);
                        handleSendDirect(opt);
                      }}>{opt}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Folder plan gate: agent wants user to confirm before filing documents */}
            {folderPlanGate && (
              <FolderPlanCard
                plan={folderPlanGate}
                applying={folderPlanApplying}
                onApply={async () => {
                  setFolderPlanApplying(true);
                  try {
                    await api.post(
                      `/exploration/case/${caseId}/conversations/${chatConversationId}/apply-folder-plan`
                    );
                    setFolderPlanGate(null);
                    dispatch({
                      type: 'APPEND_CHAT',
                      msg: {
                        role: 'assistant',
                        content: `✅ Folder plan applied — ${folderPlanGate.total_documents} document${folderPlanGate.total_documents !== 1 ? 's' : ''} organized into ${folderPlanGate.folders.length} folder${folderPlanGate.folders.length !== 1 ? 's' : ''}.`,
                      },
                    });
                  } catch (err) {
                    dispatch({
                      type: 'APPEND_CHAT',
                      msg: {
                        role: 'assistant',
                        content: `⚠️ Failed to apply folder plan: ${err?.message ?? 'Unknown error'}`,
                      },
                    });
                    setFolderPlanGate(null);
                  } finally {
                    setFolderPlanApplying(false);
                  }
                }}
                onCancel={() => {
                  setFolderPlanGate(null);
                  dispatch({
                    type: 'APPEND_CHAT',
                    msg: {
                      role: 'assistant',
                      content: 'Folder plan dismissed. You can ask me to apply it at any time.',
                    },
                  });
                }}
              />
            )}

            {/* Scope-confirmation prompt (from propose_query_scope).
                Rendered after the run finalises so the user can
                redirect the next turn without retyping. */}
            {proposedScope && !streaming && (
              <ScopeConfirmationPrompt
                scope={proposedScope}
                onPick={(text) => {
                  setProposedScope(null);
                  handleSendDirect(text);
                }}
                onDismiss={() => setProposedScope(null)}
              />
            )}

            {/* Follow-up suggestions */}
            {followUpSuggestions.length > 0 && !streaming && (
              <div className="followup-suggestions">
                <span className="followup-label">Suggested follow-ups:</span>
                <div className="followup-buttons">
                  {followUpSuggestions.map((s, i) => (
                    <button key={i} className="btn btn-sm btn-suggestion" onClick={() => {
                      setFollowUpSuggestions([]);
                      handleSendDirect(s);
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Streaming state */}
            {streaming && (
              <div className="chat-message chat-message-assistant chat-streaming">
                <div className="chat-message-header">
                  <span className="chat-role">{AGENT_NAME_SHORT}</span>
                  <ArcOrb size={18} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
                </div>

                {streamProgress.length > 0 && (
                  <div className="stream-progress">
                    {streamProgress.slice(-4).map((p, i) => (
                      <div key={i} className={`progress-step progress-${p.type}`}>
                        {p.type === 'finding' ? '💡' : p.type === 'step_started' ? <ArcOrb size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 3 }} /> : '✓'} {p.text}
                      </div>
                    ))}
                    <div ref={progressEndRef} />
                  </div>
                )}

                {staleWarning && (
                  <div className="alert alert-warning stale-warning">
                    <div>
                      <strong>⚠️ {AGENT_NAME_SHORT} appears stuck</strong> — no activity for 2 minutes.
                      <div style={{fontSize: 12, marginTop: 4, opacity: 0.8}}>
                        The operation may have exceeded the server time limit. Click Stop to cancel, then retry with a smaller request.
                      </div>
                    </div>
                    <button className="btn btn-sm btn-danger" style={{marginLeft: 8}} onClick={handleCancel}>Stop</button>
                  </div>
                )}

                {streamText && (
                  <div className="chat-message-body">
                    <ReactMarkdown
                      components={{
                        a: ({ href, children, ...props }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                        ),
                      }}
                    >{streamText.replace(/\n?---CITATIONS---[\s\S]*$/, '')}</ReactMarkdown>
                    <span className="cursor">▌</span>
                  </div>
                )}

                {streamError && (
                  <div className="alert alert-error">{streamError}</div>
                )}
              </div>
            )}

            {/* Error shown after streaming stops (e.g. rate limit, timeout) */}
            {!streaming && streamError && (
              <div className="chat-message chat-message-assistant">
                <div className="alert alert-error">⚠️ {streamError}</div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <form className="chat-input-form" onSubmit={handleSend}>
            <input
              ref={inputRef}
              className="input chat-input"
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={placeholder}
              disabled={streaming}
              autoComplete="off"
            />
            {streaming ? (
              <button type="button" className="btn btn-danger" onClick={handleCancel}>
                Stop
              </button>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={!input.trim()}>
                Send
              </button>
            )}
          </form>
          {profile?.chat_notice && (
            <div className="chat-provider-notice" data-testid="chat-provider-notice">{profile.chat_notice}</div>
          )}
          {pricing?.show_costs !== false && pricing?.show_llm_costs !== false && chatCostUsd > 0.0001 && (
            <div className="chat-cost-footer">{formatCost(chatCostUsd, marginMultiplier)} this conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
